import { ReactNode } from 'react';
import { Grid, Typography } from '@forgeglobal/design-framework';
import DialogLayout from '@tcs/client-portal-common/components/organisms/DialogLayout';
import { AssetValuationDetails } from '@tcs/client-portal-api/types/AssetValuationDetails';
import useStyles from './style';
import { ValuationForm } from './Form';
import formatDate from '@tcs/common/utils/formatDate';
import formatCurrency from '@tcs/common/utils/formatCurrency';
import { Unicode } from '@tcs/common/utils/unicode';

interface Props {
  accountNumber: string;
  asset: AssetValuationDetails;
  banner: ReactNode;
  setShowDetails: (showDetails: boolean) => void;
  dialogName: string;
}

const ValuationAssetDetail = ({
  accountNumber,
  asset,
  banner,
  setShowDetails,
  dialogName,
}: Props) => {
  const classes = useStyles();

  const { valuationDate, assetType, name, currentValue, totalUnits } = asset;

  const onSubmit = async (status: 'Success' | 'Failure') => {
    setShowDetails(false);
  };
  return (
    <DialogLayout
      setShowDialog={setShowDetails}
      shouldShowUser
      name={dialogName}>
      <Grid item container className={classes.container}>
        <Grid item>
          <Typography
            data-test-id="title"
            variant="h5"
            className={classes.title}>
            Update Value for Your Asset
          </Typography>
          <Typography
            data-test-id="accountNumber"
            variant="h6"
            className={classes.accountNumber}>
            Account Number: {accountNumber}
          </Typography>
          <div>
            <Typography
              data-test-id="sub-title"
              variant="h6"
              className={classes.subtitle}>
              Asset Details
            </Typography>
            <div>{banner}</div>
            <div className={classes.assetInfo}>
              <Typography
                data-test-id="asset-description-header"
                variant="body1"
                color="textSecondary">
                Asset description
              </Typography>
              <Typography
                variant="body1"
                className={classes.assetData}
                data-test-id="asset-name">
                {name}
              </Typography>

              <Typography
                data-test-id="asset-type-header"
                variant="body1"
                color="textSecondary">
                Asset type
              </Typography>
              <Typography
                variant="body1"
                className={classes.assetData}
                data-test-id="detail-asset-type">
                {assetType}
              </Typography>

              <Typography
                data-test-id="latest-valuation-update-header"
                variant="body1"
                color="textSecondary">
                Latest valuation update
              </Typography>
              <Typography
                variant="body1"
                className={classes.assetData}
                data-test-id="latest-valuation-date">
                {formatDate(valuationDate) || Unicode.EmDash}
              </Typography>

              {assetType === 'RealEstate' && (
                <>
                  <Typography
                    data-test-id="current-value-header"
                    variant="body1"
                    color="textSecondary">
                    Your latest value
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.assetData}
                    data-test-id="current-value"
                    data-private>
                    {formatCurrency(currentValue)}
                  </Typography>

                  <Typography
                    data-test-id="property-value-header"
                    variant="body1"
                    color="textSecondary">
                    Total property value
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.assetData}
                    data-test-id="property-value"
                    data-private>
                    {formatCurrency((100 / totalUnits) * currentValue)}
                  </Typography>

                  <Typography
                    data-test-id="your-ownership-value-header"
                    variant="body1"
                    color="textSecondary">
                    Your % ownership
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.assetData}
                    data-test-id="your-ownership"
                    data-private>
                    {totalUnits}%
                  </Typography>
                </>
              )}

              {assetType !== 'RealEstate' && (
                <>
                  <Typography
                    data-test-id="current-value-header"
                    variant="body1"
                    color="textSecondary">
                    Latest value
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.assetData}
                    data-test-id="current-value"
                    data-private>
                    {formatCurrency(currentValue)}
                  </Typography>
                </>
              )}
            </div>
          </div>
          <ValuationForm
            onCancel={() => setShowDetails(false)}
            onSubmit={onSubmit}
            asset={asset}
            accountNumber={accountNumber}
          />
        </Grid>
      </Grid>
    </DialogLayout>
  );
};

export default ValuationAssetDetail;
